import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, MapPin, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { buildApiUrl, API_CONFIG } from '../config/api';

interface Station {
  id: number;
  name: string;
  code?: string;
  line_id?: number;
  line_name?: string;
  company_name?: string;
  region_name?: string;
  lat?: string | number;
  long?: string | number;
  status?: string | null;
  order_in_line?: number;
  created_at?: string;
  updated_at?: string;
}

interface Line {
  id: number;
  name: string;
  code?: string;
  company_name?: string;
}

const StationsManager: React.FC = () => {
  const [stations, setStations] = useState<Station[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingStation, setEditingStation] = useState<Station | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [newStation, setNewStation] = useState<Partial<Station>>({
    name: '',
    code: '',
    line_id: undefined,
    lat: undefined,
    long: undefined,
    status: 'active',
    order_in_line: undefined,
  });

  // Fetch lines once on mount
  useEffect(() => {
    fetchLines();
  }, []);

  // Fetch stations when page or search changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchStations();
    }, searchTerm ? 1000 : 0); // Debounce only for search, immediate for page change

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    if (searchTerm && currentPage !== 1) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchStations = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: itemsPerPage.toString(),
      });

      // Add search parameter if search term exists
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.STATIONS}/?${params}`));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setStations(data.stations || []);
      setTotalCount(data.total_count || 0);
    } catch (error) {
      console.error('Error fetching stations:', error);
      setStations([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchLines = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.LINES));
      const data = await response.json();
      setLines(data.lines || []);
    } catch (error) {
      console.error('Error fetching lines:', error);
      setLines([]);
    }
  };

  const handleCreate = async () => {
    try {
      if (!newStation.line_id) {
        alert('Please select a line for this station.');
        return;
      }

      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.STATIONS), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newStation),
      });

      if (response.ok) {
        await fetchStations();
        setIsCreating(false);
        setNewStation({
          name: '',
          code: '',
          line_id: undefined,
          lat: undefined,
          long: undefined,
          status: 'active',
          order_in_line: undefined
        });
      } else {
        const errorData = await response.json();
        alert(`Error creating station: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating station:', error);
      alert('Error creating station. Please try again.');
    }
  };

  const handleUpdate = async (station: Station) => {
    try {
      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.STATIONS}/${station.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(station),
      });

      if (response.ok) {
        await fetchStations();
        setEditingStation(null);
      }
    } catch (error) {
      console.error('Error updating station:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this station?')) return;

    try {
      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.STATIONS}/${id}`), {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchStations();
      }
    } catch (error) {
      console.error('Error deleting station:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <MapPin className="h-6 w-6 mr-2 text-admin-600" />
          Stations Management
        </h2>
        <button
          onClick={() => setIsCreating(true)}
          className="admin-btn-primary flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Station
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search stations by name, code, line, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-input pl-10 w-full max-w-md"
          />
        </div>
      </div>

      {/* Create Form */}
      {isCreating && (
        <div className="admin-card mb-6">
          <h3 className="text-lg font-semibold mb-4">Create New Station</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Station Name</label>
              <input
                type="text"
                placeholder="e.g., Siam Station"
                value={newStation.name}
                onChange={(e) => setNewStation({ ...newStation, name: e.target.value })}
                className="admin-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Station Code</label>
              <input
                type="text"
                placeholder="e.g., S1"
                value={newStation.code || ''}
                onChange={(e) => setNewStation({ ...newStation, code: e.target.value })}
                className="admin-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Train Line *</label>
              <select
                value={newStation.line_id || ''}
                onChange={(e) => setNewStation({ ...newStation, line_id: parseInt(e.target.value) || undefined })}
                className="admin-input"
                required
              >
                <option value="">Select a line...</option>
                {lines.map((line) => (
                  <option key={line.id} value={line.id}>
                    {line.name} {line.code ? `(${line.code})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
              <input
                type="number"
                step="0.000001"
                placeholder="e.g., 13.7563"
                value={newStation.lat || ''}
                onChange={(e) => setNewStation({ ...newStation, lat: parseFloat(e.target.value) || undefined })}
                className="admin-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
              <input
                type="number"
                step="0.000001"
                placeholder="e.g., 100.5018"
                value={newStation.long || ''}
                onChange={(e) => setNewStation({ ...newStation, long: parseFloat(e.target.value) || undefined })}
                className="admin-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order in Line</label>
              <input
                type="number"
                min="1"
                placeholder="e.g., 1, 2, 3..."
                value={newStation.order_in_line || ''}
                onChange={(e) => setNewStation({ ...newStation, order_in_line: parseInt(e.target.value) || undefined })}
                className="admin-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={newStation.status || 'active'}
                onChange={(e) => setNewStation({ ...newStation, status: e.target.value })}
                className="admin-input"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="construction">Under Construction</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleCreate} className="btn-primary flex items-center">
              <Save className="h-4 w-4 mr-2" />
              Save
            </button>
            <button
              onClick={() => {
                setIsCreating(false);
                setNewStation({
                  name: '',
                  code: '',
                  line_id: undefined,
                  lat: undefined,
                  long: undefined,
                  status: 'active',
                  order_in_line: undefined
                });
              }}
              className="btn-secondary flex items-center"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Stations Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="w-1/12 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="w-1/8 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                <th className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line</th>
                <th className="w-1/6 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="w-1/8 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="w-1/12 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stations.map((station) => (
                <tr key={station.id}>
                  <td className="px-4 py-4 w-1/6">
                    {editingStation?.id === station.id ? (
                      <input
                        type="text"
                        value={editingStation.name}
                        onChange={(e) => setEditingStation({ ...editingStation, name: e.target.value })}
                        className="admin-input text-sm w-full"
                      />
                    ) : (
                      <div className="text-sm font-medium text-gray-900 truncate" title={station.name}>
                        {station.name}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 w-1/12">
                    {editingStation?.id === station.id ? (
                      <input
                        type="text"
                        value={editingStation.code || ''}
                        onChange={(e) => setEditingStation({ ...editingStation, code: e.target.value })}
                        className="admin-input text-sm w-full min-w-20"
                        placeholder="Code"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">{station.code || '-'}</div>
                    )}
                  </td>
                  <td className="px-4 py-4 w-1/8">
                    {editingStation?.id === station.id ? (
                      <input
                        type="number"
                        min="1"
                        value={editingStation.order_in_line || ''}
                        onChange={(e) => setEditingStation({ ...editingStation, order_in_line: parseInt(e.target.value) || undefined })}
                        className="admin-input text-sm w-full min-w-16"
                        placeholder="Order"
                      />
                    ) : (
                      <div className="text-sm text-gray-900 text-center">
                        {station.order_in_line ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {station.order_in_line}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 w-1/6">
                    {editingStation?.id === station.id ? (
                      <select
                        value={editingStation.line_id || ''}
                        onChange={(e) => setEditingStation({ ...editingStation, line_id: parseInt(e.target.value) || undefined })}
                        className="admin-input text-sm w-full"
                      >
                        <option value="">Select a line...</option>
                        {lines.map((line) => (
                          <option key={line.id} value={line.id}>
                            {line.name} {line.code ? `(${line.code})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-sm text-gray-900 truncate" title={station.line_name || `Line ${station.line_id}`}>
                        {station.line_name || `Line ${station.line_id}`}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 w-1/6">
                    {editingStation?.id === station.id ? (
                      <div className="space-y-1">
                        <input
                          type="number"
                          step="0.000001"
                          placeholder="Latitude"
                          value={editingStation.lat || ''}
                          onChange={(e) => setEditingStation({ ...editingStation, lat: parseFloat(e.target.value) || undefined })}
                          className="admin-input text-xs w-full"
                        />
                        <input
                          type="number"
                          step="0.000001"
                          placeholder="Longitude"
                          value={editingStation.long || ''}
                          onChange={(e) => setEditingStation({ ...editingStation, long: parseFloat(e.target.value) || undefined })}
                          className="admin-input text-xs w-full"
                        />
                      </div>
                    ) : (
                      station.lat && station.long ? (
                        <div className="text-xs text-gray-600">
                          <div className="truncate" title={`Latitude: ${typeof station.lat === 'number' ? station.lat.toFixed(6) : station.lat}`}>
                            {typeof station.lat === 'number' ? station.lat.toFixed(4) : station.lat}
                          </div>
                          <div className="truncate" title={`Longitude: ${typeof station.long === 'number' ? station.long.toFixed(6) : station.long}`}>
                            {typeof station.long === 'number' ? station.long.toFixed(4) : station.long}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Not set</span>
                      )
                    )}
                  </td>
                  <td className="px-4 py-4 w-1/8">
                    {editingStation?.id === station.id ? (
                      <select
                        value={editingStation.status || 'active'}
                        onChange={(e) => setEditingStation({ ...editingStation, status: e.target.value })}
                        className="admin-input text-xs w-full"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="construction">Under Construction</option>
                      </select>
                    ) : (
                      <div className="text-xs">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          station.status === 'active' ? 'bg-green-100 text-green-800' :
                          station.status === 'inactive' ? 'bg-red-100 text-red-800' :
                          station.status === 'construction' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {station.status || 'active'}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 w-1/12">
                    {editingStation?.id === station.id ? (
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleUpdate(editingStation)}
                          className="text-green-600 hover:text-green-900"
                          title="Save"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingStation(null)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex space-x-1">
                        <button
                          onClick={() => setEditingStation(station)}
                          className="text-admin-600 hover:text-admin-900"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(station.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200">
          <div className="flex items-center text-sm text-gray-700">
            <span>
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)} to{' '}
              {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} results
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.ceil(totalCount / itemsPerPage) }, (_, i) => i + 1)
                .filter(page =>
                  page === 1 ||
                  page === Math.ceil(totalCount / itemsPerPage) ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                )
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="px-2 text-gray-400">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-admin-600 text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))
              }
            </div>
            <button
              onClick={() => setCurrentPage(Math.min(Math.ceil(totalCount / itemsPerPage), currentPage + 1))}
              disabled={currentPage === Math.ceil(totalCount / itemsPerPage)}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {totalCount === 0 && !loading && (
        <div className="admin-card text-center py-8">
          <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">
            {searchTerm ? `No stations found matching "${searchTerm}"` : "No stations found."}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setIsCreating(true)}
              className="admin-btn-primary mt-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Station
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StationsManager;